import React, { useState, useEffect, useMemo } from 'react';
import { useInfluencers } from '@/hooks/useInfluencers';
import { useInfluencerEnrichmentRealtime } from '@/hooks/useInfluencerEnrichmentRealtime';
import { useInfluencerRefresh } from '@/hooks/useInfluencerRefresh';
import {
  INITIAL_INFLUENCER_FILTERS,
  FiltersSidebar,
  InfluencerCRMHeader,
  InfluencerPagination,
  InfluencerContentArea,
  InfluencerModals,
  SelectPromptBanner,
  useInfluencerSelection,
  type InfluencerFilters,
  type InfluencerViewMode,
} from './influencer-crm';

const InfluencerCRM: React.FC = () => {
  useInfluencerEnrichmentRealtime();
  const refresh = useInfluencerRefresh();

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<InfluencerFilters>(INITIAL_INFLUENCER_FILTERS);

  // View mode state — persisted to localStorage
  const [viewMode, setViewMode] = useState<InfluencerViewMode>(() => {
    const savedViewMode = localStorage.getItem('influencerViewMode');
    return (savedViewMode as InfluencerViewMode) || 'table';
  });

  useEffect(() => {
    localStorage.setItem('influencerViewMode', viewMode);
  }, [viewMode]);

  // Modal states
  const [addInfluencerOpen, setAddInfluencerOpen] = useState(false);
  const [addToCampaignOpen, setAddToCampaignOpen] = useState(false);
  const [importCsvOpen, setImportCsvOpen] = useState(false);
  const [exportSheetsOpen, setExportSheetsOpen] = useState(false);

  // Fetch influencers from Supabase with filters and pagination
  const { data: influencersResponse, isLoading, error } = useInfluencers({
    searchTerm,
    gender: filters.gender,
    platform: filters.platform,
    engagement: filters.engagement,
    location: filters.location,
    tier: filters.tier,
    status: filters.status,
    page: currentPage,
    pageSize,
  });

  const influencers = useMemo(() => influencersResponse?.data || [], [influencersResponse]);
  const totalCount = influencersResponse?.totalCount || 0;
  const totalPages = influencersResponse?.totalPages || 0;

  // Reset to first page when search term, filters, or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    filters.gender,
    filters.platform,
    filters.engagement,
    filters.location,
    filters.tier,
    filters.status,
    pageSize,
  ]);

  // Quick list for duplicate checking (for AddInfluencerModal)
  const influencerQuickList = useMemo(
    () =>
      influencers.map((i) => ({
        handle: i.handle,
        email: '',
      })),
    [influencers]
  );

  // Selection hook encapsulating selection logic & actions
  const {
    selectedInfluencers,
    refreshingInfluencerId,
    showSelectInfluencersPrompt,
    setShowSelectInfluencersPrompt,
    isAllSelected,
    handleSelectAll,
    handleSelectInfluencer,
    handleRefreshSelected,
    handleRefreshInfluencer,
    handleDeleteSelected,
    selectedInfluencerObjs,
    clearSelection,
  } = useInfluencerSelection({
    influencers,
    totalCount,
    searchTerm,
    filters,
    refresh,
  });

  return (
    <div className="h-screen bg-gray-50 flex">
      <SelectPromptBanner show={showSelectInfluencersPrompt} />

      <InfluencerModals
        addInfluencerOpen={addInfluencerOpen}
        onAddInfluencerOpenChange={setAddInfluencerOpen}
        influencerQuickList={influencerQuickList}
        onOpenImportCsv={() => {
          setAddInfluencerOpen(false);
          setImportCsvOpen(true);
        }}
        importCsvOpen={importCsvOpen}
        onImportCsvOpenChange={setImportCsvOpen}
        addToCampaignOpen={addToCampaignOpen}
        onAddToCampaignOpenChange={setAddToCampaignOpen}
        selectedInfluencerObjs={selectedInfluencerObjs}
        selectedInfluencerIds={selectedInfluencers}
        onAssignedToCampaign={clearSelection}
        exportSheetsOpen={exportSheetsOpen}
        onExportSheetsOpenChange={setExportSheetsOpen}
        filters={filters}
        searchTerm={searchTerm}
        totalCount={totalCount}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <InfluencerCRMHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          selectedCount={selectedInfluencers.length}
          isPolling={refresh.isPolling}
          isEnqueueing={refresh.isEnqueueing}
          canRefreshSelected={refresh.canRefreshSelected}
          refreshSelectedCount={refresh.selectedCount}
          maxSelectedRefreshes={refresh.maxSelectedRefreshes}
          refreshProgress={refresh.refreshProgress}
          onRefreshSelected={handleRefreshSelected}
          onRequestSelectInfluencers={() => setShowSelectInfluencersPrompt(true)}
          onExport={() => setExportSheetsOpen(true)}
          onAddToCampaign={() => setAddToCampaignOpen(true)}
          onAddInfluencer={() => setAddInfluencerOpen(true)}
          onDeleteSelected={handleDeleteSelected}
        />

        <InfluencerContentArea
          isLoading={isLoading}
          error={error}
          viewMode={viewMode}
          influencers={influencers}
          selectedInfluencers={selectedInfluencers}
          isAllSelected={isAllSelected}
          onSelectAll={handleSelectAll}
          onSelectInfluencer={handleSelectInfluencer}
          onRefreshInfluencer={handleRefreshInfluencer}
          refreshingInfluencerId={refreshingInfluencerId}
        />

        <InfluencerPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      <FiltersSidebar
        open={showFilters}
        filters={filters}
        setFilters={setFilters}
        setShowFilters={setShowFilters}
      />
    </div>
  );
};

export default InfluencerCRM;
